import type { INavListItem } from '@/api/types/public';
import ImageNavigationNav from './ImageNavigationNav.pc';
import LensProcessNavPc from './LensProcessNav.pc';
import ResourceSlotNav from './ResourceSlotNav.pc';
import TextImageNavigationLayout from './TextImageNavigationLayout.pc';
import TextLensNavigationLayout from './TextLensNavigationLayout.pc';
import TextLensResourceNavigationLayout from './TextLensResourceNavigationLayout.pc';
import TextResourceNavigationLayout from './TextResourceNavigationLayout.pc';
import styles from './index.module.scss';
import TextNav from './TextNav';
import { getImageNavigationSpmItemOffsets, getTextNavSpmItemOffsets } from './submenuSpm';

interface SideNavDropdownProps {
  navData: INavListItem[];
  isDarkMode: boolean;
  activeTabIndex: number;
  showDropdown: boolean;
  onDropdownMouseEnter: () => void;
  onDropdownMouseLeave: () => void;
}

const SideNavDropdown = ({
  navData,
  isDarkMode,
  activeTabIndex,
  showDropdown,
  onDropdownMouseEnter,
  onDropdownMouseLeave,
}: SideNavDropdownProps) => {
  return (
    <>
      {navData.map((navItem, index) => {
        if (!navItem.children || navItem.children.length === 0) return null;

        const isActive = showDropdown && activeTabIndex === index;

        const textNavItems = navItem.children.filter((item) => item.template_id === 2);
        const lensProcessItems = navItem.children.filter((item) => item.template_id === 4);
        const resourceSlotItems = navItem.children.filter(
          (item) => item.template_id === 5 && item.content?.resource_image,
        );
        const imageNavigationItems = navItem.children.filter(
          (item) =>
            item.template_id === 3 &&
            item.content?.list?.some((listItem) => Boolean(listItem.jump_image)),
        );
        const textImageNavigationItems = navItem.children.filter(
          (item) =>
            item.template_id === 2 ||
            (item.template_id === 3 &&
              item.content?.list?.some((listItem) => Boolean(listItem.jump_image))),
        );
        const hasResourceAndImageNavigation =
          resourceSlotItems.length > 0 && imageNavigationItems.length > 0;
        const hasLensProcess = lensProcessItems.length > 0;
        // 扁平坑位序号（submenu_item_GG_II 的 II）：文字区块按渲染顺序累计条目，图片分类接在其后。
        const textNavSpmItemOffsets = getTextNavSpmItemOffsets(textNavItems);
        const textNavSpmItemTotal = textNavItems.reduce(
          (total, item) => total + (item.content?.list?.length || 0),
          0,
        );
        const imageNavSpmItemOffsets = getImageNavigationSpmItemOffsets(imageNavigationItems);
        const usesTextImageNavigationLayout =
          textNavItems.length > 0 &&
          imageNavigationItems.length > 0 &&
          !hasLensProcess &&
          resourceSlotItems.length === 0;
        const usesTextResourceNavigationLayout =
          textNavItems.length > 0 &&
          resourceSlotItems.length > 0 &&
          !hasLensProcess &&
          imageNavigationItems.length === 0;
        const usesTextLensNavigationLayout =
          textNavItems.length > 0 &&
          lensProcessItems.length > 0 &&
          imageNavigationItems.length === 0 &&
          resourceSlotItems.length === 0;
        const usesTextLensResourceNavigationLayout =
          textNavItems.length > 0 &&
          lensProcessItems.length > 0 &&
          resourceSlotItems.length > 0 &&
          imageNavigationItems.length === 0;

        return (
          <div
            key={`dropdown-${navItem.id}`}
            className={`absolute left-[0] top-[60px] z-[202] max-h-[50vh] w-screen overflow-hidden ${isDarkMode ? 'bg-[#131415]' : 'bg-white'} flex items-stretch justify-between box-border shadow-md ${hasResourceAndImageNavigation ? 'flex-wrap' : ''}`}
            style={{
              boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.08)',
              display: isActive ? 'flex' : 'none',
            }}
            onMouseEnter={onDropdownMouseEnter}
            onMouseLeave={onDropdownMouseLeave}
          >
            {usesTextImageNavigationLayout ? (
              <TextImageNavigationLayout
                navigationItems={textImageNavigationItems}
                firstLevelIndex={index}
                firstLevelTitle={navItem.content?.nav_name || ''}
                isDarkMode={isDarkMode}
                isActive={isActive}
              />
            ) : usesTextLensResourceNavigationLayout ? (
              <TextLensResourceNavigationLayout
                textNavItems={textNavItems}
                lensProcessItems={lensProcessItems}
                resourceSlotItems={resourceSlotItems}
                firstLevelIndex={index}
                firstLevelTitle={navItem.content?.nav_name || ''}
                isDarkMode={isDarkMode}
                isActive={isActive}
              />
            ) : usesTextResourceNavigationLayout ? (
              <TextResourceNavigationLayout
                textNavItems={textNavItems}
                resourceSlotItems={resourceSlotItems}
                firstLevelIndex={index}
                firstLevelTitle={navItem.content?.nav_name || ''}
                isDarkMode={isDarkMode}
                isActive={isActive}
              />
            ) : usesTextLensNavigationLayout ? (
              <TextLensNavigationLayout
                textNavItems={textNavItems}
                lensProcessItems={lensProcessItems}
                firstLevelIndex={index}
                firstLevelTitle={navItem.content?.nav_name || ''}
                isDarkMode={isDarkMode}
                isActive={isActive}
              />
            ) : (
              <div
                className={`flex min-w-0 flex-1 items-start gap-[80px] overflow-hidden px-[48px] pt-[20px] min-[1920px]:gap-[160px] ${hasLensProcess || (imageNavigationItems.length > 0 && resourceSlotItems.length === 0) ? 'pb-[32px]' : 'pb-[24px]'}`}
              >
                {textNavItems.map((item, idx) => {
                  return (
                    <div key={`${item.id}-${item.template_id}`} className="flex-shrink-0">
                      <TextNav
                        item={item}
                        firstLevelIndex={index}
                        firstLevelTitle={navItem.content?.nav_name || ''}
                        secondLevelIndex={idx}
                        isLast={idx === textNavItems.length - 1}
                        isDarkMode={isDarkMode}
                        isActive={isActive}
                        spmItemOffset={textNavSpmItemOffsets.get(item) ?? 0}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {!usesTextResourceNavigationLayout &&
            !usesTextLensNavigationLayout &&
            !usesTextLensResourceNavigationLayout &&
            hasLensProcess ? (
              <div
                className={`ml-auto flex shrink-0 items-stretch ${styles.lensProcessContainer} ${resourceSlotItems.length === 0 && imageNavigationItems.length === 0 ? styles.lensProcessRightInset : ''}`}
              >
                {lensProcessItems.map((item) => (
                  <LensProcessNavPc
                    key={`${item.id}-${item.template_id}`}
                    isDarkMode={isDarkMode}
                  />
                ))}
                {resourceSlotItems.map((item, resourceIndex) => (
                  <ResourceSlotNav
                    key={`${item.id}-${item.template_id}`}
                    item={item}
                    firstLevelIndex={index}
                    firstLevelTitle={navItem.content?.nav_name || ''}
                    resourceIndex={resourceIndex}
                    spmItemOffset={textNavSpmItemTotal + resourceIndex}
                  />
                ))}
              </div>
            ) : !usesTextResourceNavigationLayout &&
              !usesTextLensNavigationLayout &&
              !usesTextLensResourceNavigationLayout &&
              resourceSlotItems.length > 0 ? (
              <div className={`ml-auto flex shrink-0 ${styles.resourceSlotContainer}`}>
                {resourceSlotItems.map((item, resourceIndex) => (
                  <ResourceSlotNav
                    key={`${item.id}-${item.template_id}`}
                    item={item}
                    firstLevelIndex={index}
                    firstLevelTitle={navItem.content?.nav_name || ''}
                    resourceIndex={resourceIndex}
                    spmItemOffset={textNavSpmItemTotal + resourceIndex}
                  />
                ))}
              </div>
            ) : null}

            {imageNavigationItems.length > 0 && !usesTextImageNavigationLayout ? (
              <div
                className={`flex shrink-0 items-start gap-[32px] pb-[32px] pt-[20px] ${
                  hasResourceAndImageNavigation
                    ? 'w-full justify-end px-[48px]'
                    : 'ml-auto pr-[48px]'
                }`}
              >
                {imageNavigationItems.map((item) => (
                  <ImageNavigationNav
                    key={`${item.id}-${item.template_id}`}
                    item={item}
                    firstLevelIndex={index}
                    firstLevelTitle={navItem.content?.nav_name || ''}
                    isDarkMode={isDarkMode}
                    // 该组合分支里图片渲染在资源位之后：偏移叠加资源位占号（每个资源位固定 1 号）。
                    spmItemOffset={
                      textNavSpmItemTotal +
                      resourceSlotItems.length +
                      (imageNavSpmItemOffsets.get(item) ?? 0)
                    }
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
};

export default SideNavDropdown;
